import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import {
    createContext,
    useContext,
    useEffect,
    useState
} from 'react'
import { gsap, ScrollTrigger } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'

interface SmoothScrollProps {
    children: ReactNode
}

const LenisContext = createContext<Lenis | null>(null)

let activeLenis: Lenis | null = null

/** The live smooth scroll instance, for code that runs outside React (click handlers, timelines). */
export const getLenis = (): Lenis | null => activeLenis

const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        const instance = new Lenis({
            lerp: 0.1,
            smoothWheel: !prefersReducedMotion(),
            autoRaf: false,
            anchors: false,
            stopInertiaOnNavigate: true
        })

        const onFrame = (time: number): void => instance.raf(time * 1000)
        const offScroll = instance.on('scroll', () => ScrollTrigger.update())

        gsap.ticker.add(onFrame)
        gsap.ticker.lagSmoothing(0)

        if (document.documentElement.classList.contains('is-transitioning')) instance.stop()

        activeLenis = instance
        setLenis(instance)

        return () => {
            offScroll()
            gsap.ticker.remove(onFrame)
            instance.destroy()
            activeLenis = null
        }
    }, [])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}

export const useLenis = (): Lenis | null => useContext(LenisContext)

export default SmoothScroll
